import React, { useMemo } from 'react'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, isLPCAdvancedSortingEnabled, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { useAppSelector } from 'hooks/reduxHooks'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import usePropertyBedbankViews from 'hooks/OfferPage/usePropertyBedbankViews'
import useOffer from 'hooks/Offers/useOffer'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PropertyRoomOptionMobile from 'components/OfferPage/Property/MobileBookingFlow/PropertyRoomOptionMobile'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getPackageOptionsByRoom } from 'components/OfferPage/Property/SinglePropertyUtils'
import AccommodationMobileHeaderFilters from 'components/OfferPage/Accommodation/AccommodationMobileHeaderFilters'

interface Props {
  room: App.PackageRoomType;
  selectedBedbankRoomId?: string;
  offer: App.HotelOffer;
  airports?: Array<App.Airport>;
  packageOptions: Array<OfferPackageOptionView>;
  onClose?: () => void;
  onBack?: () => void;
}

function SinglePropertyPackageSelectionModal(props: Props) {
  const {
    room,
    selectedBedbankRoomId,
    offer,
    airports,
    packageOptions,
    onClose,
    onBack,
  } = props

  const LPCAdvancedSortingEnabled = useAppSelector(isLPCAdvancedSortingEnabled)

  const [bedbankPackageViews, fetchingBedbankViews] = usePropertyBedbankViews(offer.property.id)
  const [mapping] = usePropertyOfferMapping(offer.property.id)
  const [bedbankOffer] = useOffer<App.BedbankOffer>(mapping?.bedbankOfferId)

  // Get the mapping for this property page so we know what bedbank offer to fetch
  const packageOptionsByRoom = useMemo(() => getPackageOptionsByRoom(packageOptions, LPCAdvancedSortingEnabled, bedbankPackageViews, mapping), [packageOptions, LPCAdvancedSortingEnabled, bedbankPackageViews, mapping])

  const selectedRoom = useMemo(() => packageOptionsByRoom.find(option => option.bedbank.some(bedbank => bedbank.package.roomId === selectedBedbankRoomId) || option.lpc.some(lpc => lpc.package.roomType?.id === room?.id)), [packageOptionsByRoom, room?.id, selectedBedbankRoomId])

  const destinationAirport = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  const roomListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [packageOptionsByRoom]) // regenerate id if list of rooms changes

  return (
    <>
      <ModalHeader
        title="Select a package"
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <AccommodationMobileHeaderFilters />
      </ModalHeader>
      <ModalBody>
        {selectedRoom && <PropertyRoomOptionMobile
          key={selectedRoom.roomId}
          offer={offer}
          packageViews={selectedRoom.lpc}
          bedbankViews={selectedRoom.bedbank}
          bedbankOffer={bedbankOffer}
          flightDestination={destinationAirport}
          roomListId={roomListId}
        />}
        {fetchingBedbankViews && <LoadingIndicator inline />}
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  room: state.room,
  selectedBedbankRoomId: state.selectedBedbankRoomId,
  offer: state.offer,
  airports: appState.geo.airports,
  packageOptions: getDurationPackageOptions(state, appState),
}))(SinglePropertyPackageSelectionModal)
